import React, { useEffect, useState } from "react";
import { Routes, Route, Link } from "react-router-dom";
import Layout from "../components/Layout";
import { api, formatError } from "../api";
import { useAuth } from "../context/AuthContext";
import { Users, Mail, Building2, BarChart3, School, Briefcase, Award, FileText, Settings } from "lucide-react";
import Analytics from "./Analytics";
import InstitutePipeline from "./InstitutePipeline";
import InstituteOutcomes from "./InstituteOutcomes";
import InstituteReports from "./InstituteReports";
import usePageTitle from "../hooks/usePageTitle";
import { KpiTile, SectionCard, DashboardHeader } from "../components/dashboard/shells";

const links = [
  { key: "home",     to: "/institution/dashboard", end: true, label: "Overview", icon: Building2 },
  { key: "students", to: "/institution/students",  label: "My Students",         icon: Users },
  { key: "invites",  to: "/institution/invites",   label: "Placement Drives",    icon: Briefcase },
  { key: "pipeline", to: "/institution/pipeline",  label: "Live Pipeline",       icon: Briefcase },
  { key: "outcomes", to: "/institution/outcomes",  label: "Placement Outcomes",  icon: Award },
  { key: "reports",  to: "/institution/reports",   label: "NAAC/NBA Reports",    icon: FileText },
  { key: "analytics",to: "/institution/analytics", label: "Analytics",           icon: BarChart3 },
];

function Overview() {
  usePageTitle("Placement Office");
  const { user } = useAuth();
  const [students, setStudents] = useState([]);
  const [profile, setProfile] = useState(null);
  const [pipeline, setPipeline] = useState([]);
  useEffect(() => { api.get("/students").then(r => setStudents(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/auth/me").then(r => setProfile(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/institute/pipeline").then(r => setPipeline(r.data || [])).catch(() => {}); }, []);
  const instType = (user?.institution_type || profile?.institution_type || "institute");
  const affUniversity = (user?.affiliated_university || profile?.affiliated_university);
  const typeLabel = { university: "University", college: "College", institute: "Institute", polytechnic: "Polytechnic" }[instType] || "Institute";
  const placed = students.filter(s => s.employability_score >= 70).length;
  const inPipeline = pipeline.length;
  const withResume = students.filter(s => s.resume_uploaded).length;

  return (
    <div className="space-y-4">
      <DashboardHeader
        eyebrow={`${typeLabel}${affUniversity && instType !== "university" ? " · affiliated to " + affUniversity : ""}`}
        title="Run your placement season, end-to-end."
        subtitle="Onboard your batch, track their skills, and introduce them to recruiters."
        primary={{ to: "/institution/invites",  label: "Invite students",   testid: "inst-cta-invite" }}
        secondary={{ to: "/institution/students", label: "Browse directory", testid: "inst-cta-browse" }}
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiTile
          icon={Users}
          label="Students on platform"
          value={students.length}
          sub="Verified enrolments"
          accent="#006CFF"
          to="/institution/students"
          testid="inst-stu-count"
          cta="Directory"
        />
        <KpiTile
          icon={Briefcase}
          label="In active pipeline"
          value={inPipeline}
          sub="With ongoing applications"
          accent="#9933FF"
          to="/institution/pipeline"
          testid="inst-pipeline"
          cta="Live"
        />
        <KpiTile
          icon={Award}
          label="Placement-ready"
          value={placed}
          sub="Employability ≥ 70"
          accent="#10B981"
          to="/institution/outcomes"
          testid="inst-placed"
          cta="Outcomes"
        />
        <KpiTile
          icon={FileText}
          label="Resumes on file"
          value={withResume}
          sub={`${students.length ? Math.round((withResume / students.length) * 100) : 0}% of batch`}
          accent="#F59E0B"
          to="/institution/reports"
          testid="inst-resumes"
          cta="Reports"
        />
      </div>
    </div>
  );
}

function Students() {
  const [list, setList] = useState([]);
  useEffect(()=>{ api.get("/students").then(r=>setList(r.data)); }, []);
  return (
    <div className="card overflow-x-auto">
      <table className="dense"><thead><tr><th>Name</th><th>Institute</th><th>Course</th><th>Graduation</th><th>CGPA</th></tr></thead>
        <tbody>{list.map(s => (<tr key={s.user_id}><td>{s.name}</td><td>{s.institute_name || "—"}</td><td>{s.course || "—"}</td><td>{s.graduation_year || "—"}</td><td>{s.cgpa || "—"}</td></tr>))}
        {list.length===0 && <tr><td colSpan={5} className="text-muted">No students yet.</td></tr>}</tbody></table>
    </div>
  );
}

function Invites() {
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("Join our institute's placement program on SkillConnect.");
  const [list, setList] = useState([]);
  const [msg, setMsg] = useState("");
  const load = () => api.get("/invitations/mine").then(r=>setList(r.data));
  useEffect(()=>{ load(); }, []);
  const send = async (e) => {
    e.preventDefault(); setMsg("");
    try { await api.post("/invitations", { student_email: email, message }); setEmail(""); setMsg("Invitation saved"); load(); }
    catch (e2) { setMsg(formatError(e2)); }
  };
  return (
    <div className="grid md:grid-cols-3 gap-6">
      <form onSubmit={send} className="card card-pad" data-testid="invite-form">
        <h3 className="font-display font-bold text-xl">New invitation</h3>
        <input className="input mt-3" placeholder="student@example.com" type="email" required value={email} onChange={(e)=>setEmail(e.target.value)} data-testid="invite-email" />
        <textarea className="input mt-3 min-h-[100px]" value={message} onChange={(e)=>setMessage(e.target.value)} data-testid="invite-msg" />
        <button className="btn-primary w-full justify-center mt-4" data-testid="invite-submit">Send invitation</button>
        <div className="text-xs text-muted mt-2" data-testid="invite-status">{msg}</div>
      </form>
      <div className="md:col-span-2 card overflow-x-auto">
        <table className="dense"><thead><tr><th>Email</th><th>Status</th><th>Sent</th></tr></thead>
        <tbody>{list.map(i => (<tr key={i.id}><td>{i.student_email}</td><td><span className="badge">{i.status}</span></td><td>{new Date(i.created_at).toLocaleDateString()}</td></tr>))}
        {list.length===0 && <tr><td colSpan={3} className="text-muted">No invitations yet.</td></tr>}</tbody></table>
      </div>
    </div>
  );
}

export default function InstituteDashboard() {
  return (
    <Layout title="Placement Office" links={links}>
      <Routes>
        <Route index element={<Overview />} />
        <Route path="students" element={<Students />} />
        <Route path="invites" element={<Invites />} />
        <Route path="pipeline" element={<InstitutePipeline />} />
        <Route path="outcomes" element={<InstituteOutcomes />} />
        <Route path="reports" element={<InstituteReports />} />
        <Route path="analytics" element={<Analytics />} />
        <Route path="*" element={<Overview />} />
      </Routes>
    </Layout>
  );
}
